import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ReferenceArrayInput } from 'react-admin';
import { Form, FormSpy } from 'react-final-form';
import GridInputChoices from '../../users/userauthorization/GridInputChoices';
import GridInput from '../../../core/components/inputs/GridInput';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { CircularProgress, Grid } from '@material-ui/core';
import { CreationInvestorContext } from './CreationInvestorContext';
import FundInvestorApi from '../../../core/apis/FundInvestorApi';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import { isEmpty } from 'lodash';
import { Alert } from '@material-ui/lab';
import { STEP } from '../InvestorCreate';

const AddRelatedFundsStep = props => {
  const {
    reduxFunds,
    onSubmitEnd,
    hasDeleteFundInvestorPermission,
    relatedFundsUploaded,
    setCurrentAuthorizations,
    setIsNextAvailable,
    setIsSpinnerOnNextButton,
    setActiveListOfSteps,
    activeListOfSteps,
    setRelatedFundsUploaded
  } = props;
  const { investorContext, setInvestorContext } = useContext(
    CreationInvestorContext
  );
  const notifyServerError = useNotifyServerError();
  const [loading, setLoading] = useState(true);

  const getInitialValues = () => {
    if (investorContext.relatedFunds !== undefined) {
      return investorContext.relatedFunds;
    } else {
      return null;
    }
  };

  function filterFundInvestorsStates(fi) {
    setInvestorContext(prev => ({
      ...prev,
      fundInvestors: [
        ...prev.fundInvestors.filter(elem => elem.fundId !== fi.fundId)
      ],
      fundIds: [...prev.fundIds.filter(id => id !== fi.fundId)]
    }));

    // We remove any permission that contains the investor removed.
    setCurrentAuthorizations(prev => [
      ...prev.filter(authorization => authorization.fundId !== fi.fundId)
    ]);
    setRelatedFundsUploaded(prev => [
      ...prev.filter(fund => fund.fundId !== fi.fundId)
    ]);
  }

  useEffect(() => {
    FundInvestorApi.getByInvestorId(investorContext.investor.id)
      .then(({ pageData }) => {
        const fundIds = pageData.map(fund => fund.fundId);
        setInvestorContext(prev => ({
          ...prev,
          relatedFunds: {
            funds: fundIds
          }
        }));
      })
      .catch(e => notifyServerError(e, undefined))
      .finally(() => setLoading(false));
  }, [investorContext.investor.id, setInvestorContext, notifyServerError]);

  const allFunds = useMemo(() => Object.values(reduxFunds), [reduxFunds]);

  const referenceArrayInputProps = {
    label: '',
    optionTextLabel: 'Fund Name',
    optionValueLabel: 'Fund ID',
    resource: 'funds',
    reference: 'funds',
    source: 'funds',
    perPage: -1,
    sort: { field: 'name', order: 'ASC' },
    additionalColumns: [
      { label: 'Status', name: 'status', isStatusColumn: true }
    ]
  };

  const onSubmit = async values => {
    setIsSpinnerOnNextButton(true);

    const investorId = investorContext.investor.id;
    let payload = [];
    values.funds.forEach(fund => {
      payload = [
        ...payload,
        {
          investorId,
          fundId: fund
        }
      ];
    });

    // First submission, we create fundInvestors
    if (isEmpty(investorContext.fundInvestors)) {
      setInvestorContext(prev => ({ ...prev, fundIds: values.funds }));
      try {
        await FundInvestorApi.create(payload).then(res => {
          if (!isEmpty(res)) {
            setIsSpinnerOnNextButton(false);
            setInvestorContext(prev => ({ ...prev, fundInvestors: res }));
            setRelatedFundsUploaded(res);
            onSubmitEnd();
          }
        });
      } catch (e) {
        notifyServerError(e, undefined);
        setIsSpinnerOnNextButton(false);
      }
    } else {
      // If not the first submission, we check if there are any fund-investors to delete/add
      const fundInvestorToDelete = investorContext.fundInvestors.reduce(
        (acc, curr) => {
          if (!payload.some(element => element.fundId === curr.fundId)) {
            acc = [...acc, curr];
          }
          return acc;
        },
        []
      );
      const fundInvestorToAdd = payload.reduce((acc, curr) => {
        if (
          !investorContext.fundInvestors.some(
            element => element.fundId === curr.fundId
          )
        ) {
          acc = [...acc, curr];
        }
        return acc;
      }, []);

      if (
        fundInvestorToDelete.length > 0 &&
        fundInvestorToAdd.length === 0 &&
        hasDeleteFundInvestorPermission
      ) {
        await FundInvestorApi.delete(fundInvestorToDelete)
          .then(() => {
            fundInvestorToDelete.forEach(fi => filterFundInvestorsStates(fi));
            setIsSpinnerOnNextButton(false);
            onSubmitEnd();
          })
          .catch(e => notifyServerError(e));
      } else if (
        fundInvestorToDelete.length === 0 &&
        fundInvestorToAdd.length > 0
      ) {
        await FundInvestorApi.create(fundInvestorToAdd)
          .then(res => {
            setInvestorContext(prev => ({
              ...prev,
              fundInvestors: [...prev['fundInvestors'], ...res],
              fundIds: values.funds
            }));
            setRelatedFundsUploaded(prev => [...prev, ...res]);
            setIsSpinnerOnNextButton(false);
            onSubmitEnd();
          })
          .catch(e => notifyServerError(e));
      } else if (
        fundInvestorToDelete.length > 0 &&
        fundInvestorToAdd.length > 0
      ) {
        if (hasDeleteFundInvestorPermission) {
          await FundInvestorApi.delete(fundInvestorToDelete).then(() => {
            fundInvestorToDelete.forEach(fi => filterFundInvestorsStates(fi));
          });
        }

        await FundInvestorApi.create(fundInvestorToAdd).then(res => {
          setInvestorContext(prev => ({
            ...prev,
            fundInvestors: [...prev.fundInvestors, ...res]
          }));
          setRelatedFundsUploaded(prev => [...prev, ...res]);
        });
        fundInvestorToAdd.forEach(fi =>
          setInvestorContext(prev => ({
            ...prev,
            fundIds: [...prev.fundIds, fi.fundIds]
          }))
        );

        setIsSpinnerOnNextButton(false);
        onSubmitEnd();
      } else {
        setIsSpinnerOnNextButton(false);
        onSubmitEnd();
      }
    }

    const updatedListOfStep = activeListOfSteps.map(step => {
      if (step.name === STEP.addRelatedFunds) {
        return { ...step, completed: true };
      }
      return step;
    });
    setActiveListOfSteps(updatedListOfStep);
  };

  const displayWarningMessage = () => {
    if (!isEmpty(relatedFundsUploaded) && !hasDeleteFundInvestorPermission) {
      return (
        <Alert severity="warning">
          You don't have the permission to remove Related Funds. If unchecked,
          they will not be removed.
        </Alert>
      );
    } else return null;
  };

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </div>
    );
  } else {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Form onSubmit={onSubmit} initialValues={getInitialValues()}>
            {({ handleSubmit }) => (
              <form onSubmit={handleSubmit} id="addRelatedFundsForm">
                <FormSpy
                  subscription={{ submitting: true, values: true }}
                  onChange={formStateProps => {
                    setIsNextAvailable(
                      !formStateProps.submitting &&
                        formStateProps.values.funds.length > 0
                    );
                  }}
                />
                <ReferenceArrayInput {...referenceArrayInputProps}>
                  <GridInputChoices
                    allFunds={allFunds}
                    emptyText="There are no Funds to be displayed."
                  >
                    <GridInput
                      height={`${document.documentElement.scrollHeight -
                        432}px`}
                    />
                  </GridInputChoices>
                </ReferenceArrayInput>
              </form>
            )}
          </Form>
          {displayWarningMessage()}
        </Grid>
      </Grid>
    );
  }
};

const mapStateToProps = state => {
  return {
    reduxFunds: get(state, 'admin.resources.funds.data', {})
  };
};
export default connect(mapStateToProps, {})(AddRelatedFundsStep);
